/**
 * The Morning and Evening Elyxeer reminders, as local notifications on this device.
 *
 * The times belong to the account (`AuthUser.morning_reminder_*`), but the phone rings
 * by itself, on a repeating wall-clock trigger. Only the device knows its timezone; a
 * daily alarm needs no server, no scheduler and no push provider; and it still rings in
 * airplane mode. The account is the source of truth and this module makes the device's
 * pending notifications match it — nothing more.
 *
 * Two rules shape everything below:
 *
 * - **Never prompt from here.** On iOS `schedule()` raises the system permission dialog
 *   by itself while the member has not answered it, and iOS grants one dialog per
 *   install. A sync runs at boot and on every resume, so it schedules only once
 *   permission is already granted. Asking is the settings screen's job, at the moment
 *   the member turns a reminder on.
 * - **Compare, then replace.** A sync that finds the device already matching the plan
 *   does nothing; otherwise it cancels both reminders and schedules what the plan says.
 *   Every sync runs on one queue, so a burst of edits settles on the last of them.
 */

import type { PluginListenerHandle } from "@capacitor/core";
import type {
  LocalNotificationSchema,
  PendingLocalNotificationSchema,
} from "@capacitor/local-notifications";

import { isNative, onResume, platform } from "~/lib/capacitor";
import { setPendingRoute } from "~/lib/pending-route";
import type { NotificationPermissionState } from "~/lib/push-notifications";
import { parseClockTime, ROUTINE_PATH } from "~/lib/routine";
import type { RoutineSlot } from "~/types/routine";

/**
 * Notification ids. Frozen: the OS files the pending request under this number, so a
 * changed id would leave the old reminder ringing beside the new one on every upgrade.
 */
const REMINDER_IDS: Record<RoutineSlot, number> = {
  morning: 30501,
  evening: 30502,
};

/** Android channel id, frozen for the same reason as the push channels. */
export const ROUTINE_REMINDER_CHANNEL_ID = "routine_reminders";

/** Marks a notification as one of ours, both in the pending list and on a tap. */
const REMINDER_TYPE = "routine_reminder";

export interface RoutineReminder {
  slot: RoutineSlot;
  /** `HH:MM`, a local wall-clock time. */
  time: string;
  title: string;
  body: string;
}

export interface RoutineReminderPlan {
  reminders: RoutineReminder[];
  /** Localised Android channel copy; the settings list shows it to the member. */
  channel: { name: string; description: string };
}

let lastPlan: RoutineReminderPlan | null = null;
let queue: Promise<void> = Promise.resolve();

function enqueue(task: () => Promise<void>): Promise<void> {
  const run = queue.catch(() => undefined).then(task);
  queue = run.catch(() => undefined);
  return run;
}

function toNotification(
  reminder: RoutineReminder,
): LocalNotificationSchema | null {
  const clock = parseClockTime(reminder.time);
  if (!clock) return null;
  return {
    id: REMINDER_IDS[reminder.slot],
    title: reminder.title,
    body: reminder.body,
    // `on` repeats: a calendar trigger on iOS, a self-rescheduling alarm on Android.
    schedule: { on: clock, allowWhileIdle: true },
    channelId: ROUTINE_REMINDER_CHANNEL_ID,
    extra: { type: REMINDER_TYPE, slot: reminder.slot, time: reminder.time },
    // Not an alarm clock: a few minutes late is harmless, and asking for an exact alarm
    // makes `schedule()` open Android's "Alarms & reminders" settings page by itself.
    isExactNotification: false,
    // Shown even with the app open — the member may be on the dashboard at 7:30, and
    // the banner is still the shortest way to the tick.
    foreground: true,
  };
}

function isOurs(id: number): boolean {
  return id === REMINDER_IDS.morning || id === REMINDER_IDS.evening;
}

function alreadyScheduled(
  pending: PendingLocalNotificationSchema[],
  wanted: LocalNotificationSchema[],
): boolean {
  const ours = pending.filter((notification) => isOurs(notification.id));
  return (
    ours.length === wanted.length &&
    wanted.every((notification) =>
      ours.some(
        (candidate) =>
          candidate.id === notification.id &&
          candidate.title === notification.title &&
          candidate.body === notification.body &&
          candidate.extra?.time === notification.extra?.time,
      ),
    )
  );
}

async function apply(plan: RoutineReminderPlan | null): Promise<void> {
  if (!isNative()) return;
  const { LocalNotifications } = await import("@capacitor/local-notifications");

  const wanted = (plan?.reminders ?? [])
    .map(toNotification)
    .filter(
      (notification): notification is LocalNotificationSchema =>
        notification !== null,
    );

  const { notifications: pending } = await LocalNotifications.getPending();
  if (alreadyScheduled(pending, wanted)) return;

  // Cancelling needs no permission, so a reminder switched off on another device stops
  // ringing here even where the member has since revoked notifications.
  await LocalNotifications.cancel({
    notifications: Object.values(REMINDER_IDS).map((id) => ({ id })),
  });
  if (wanted.length === 0) return;

  const { display } = await LocalNotifications.checkPermissions();
  // Anything but granted: scheduling would ask, and this is not the moment to.
  if (display !== "granted") return;

  if (platform() === "android" && plan) {
    await LocalNotifications.createChannel({
      id: ROUTINE_REMINDER_CHANNEL_ID,
      name: plan.channel.name,
      description: plan.channel.description,
      importance: 4,
      // The copy names a supplement, not anything private.
      visibility: 1,
      vibration: true,
    });
  }
  await LocalNotifications.schedule({ notifications: wanted });
}

/** Make this device's reminders match `plan`. `null` means "none". Never prompts. */
export function syncRoutineReminders(
  plan: RoutineReminderPlan | null,
): Promise<void> {
  lastPlan = plan;
  return enqueue(() => apply(plan));
}

/**
 * Re-apply the last plan — after the app returns to the foreground, or after the member
 * has just granted permission, either of which can turn "nothing scheduled" into
 * "schedulable" without the plan itself changing.
 */
export function resyncRoutineReminders(): Promise<void> {
  return enqueue(() => apply(lastPlan));
}

/** Stop both reminders on this device — the account is leaving it. */
export function cancelRoutineReminders(): Promise<void> {
  lastPlan = null;
  return enqueue(() => apply(null));
}

/** What the OS says about local notifications, flattened like the push equivalent. */
export async function reminderPermissionState(): Promise<NotificationPermissionState> {
  if (!isNative()) return "unsupported";
  const { LocalNotifications } = await import("@capacitor/local-notifications");
  const { display } = await LocalNotifications.checkPermissions();
  if (display === "granted") return "granted";
  if (display === "denied") return "denied";
  return "prompt";
}

/**
 * Ask for permission if the member has not answered yet. Only ever called from the
 * member turning a reminder on — the one moment the dialog is about what they just did.
 */
export async function requestReminderPermission(): Promise<NotificationPermissionState> {
  const state = await reminderPermissionState();
  if (state !== "prompt") return state;
  const { LocalNotifications } = await import("@capacitor/local-notifications");
  const { display } = await LocalNotifications.requestPermissions();
  return display === "granted"
    ? "granted"
    : display === "denied"
      ? "denied"
      : "prompt";
}

/**
 * Install the reminders' native listeners, once, at shell start.
 *
 * A tap opens the routine. It is parked through `pending-route` like a push tap, so a
 * tap that launched the app is replayed once routing can stick; the plugin holds a
 * cold-start tap until this listener exists.
 *
 * A resume re-applies the last plan. Coming back from the phone's settings is the one
 * way a member who once said no can turn notifications on, and nothing else in the app
 * would notice that the reminders have become schedulable.
 */
export async function initializeRoutineReminders(): Promise<() => void> {
  if (!isNative()) return () => undefined;
  const { LocalNotifications } = await import("@capacitor/local-notifications");
  const handle: PluginListenerHandle = await LocalNotifications.addListener(
    "localNotificationActionPerformed",
    ({ notification }) => {
      const extra = notification.extra as { type?: unknown } | undefined;
      if (extra?.type === REMINDER_TYPE) setPendingRoute(ROUTINE_PATH);
    },
  );
  const removeResume = onResume(() => void resyncRoutineReminders());
  return () => {
    void handle.remove();
    removeResume();
  };
}
